import React from "react"
import CategoryIcon from "@mui/icons-material/Category"
import ApartmentIcon from "@mui/icons-material/Apartment"
import HomeWorkIcon from "@mui/icons-material/HomeWork"
import type {PropertyPriceAnalysisRow} from "@/api/properties"

const PLACEHOLDER = "—"

const isFiniteNumber = (value: unknown): value is number =>
   typeof value === "number" && Number.isFinite(value)

const formatMoney = (value: unknown) =>
   isFiniteNumber(value)
      ? `$${value.toLocaleString("en-US", {
           minimumFractionDigits: 0,
           maximumFractionDigits: 0,
        })}`
      : PLACEHOLDER

const formatNumber = (value: unknown) =>
   isFiniteNumber(value) ? value.toLocaleString("es-MX") : PLACEHOLDER

const formatPercent = (value: unknown) =>
   isFiniteNumber(value)
      ? `${value.toLocaleString(undefined, {
           minimumFractionDigits: 2,
           maximumFractionDigits: 2,
        })}%`
      : PLACEHOLDER

const getComparisonTextColor = (direction: "below" | "above" | "equal") => {
   if (direction === "below") return "text-green"
   if (direction === "above") return "text-red"
   return "text-blue"
}

const getComparisonIcon = (direction: "below" | "above" | "equal") => {
   if (direction === "below") return "↓"
   if (direction === "above") return "↑"
   return ""
}

const rowColorClassName: Record<PropertyPriceAnalysisRow["key"], string> = {
   category: "text-blue",
   condo: "text-green",
   property: "text-green",
}

const rowIcons: Record<PropertyPriceAnalysisRow["key"], React.ReactNode> = {
   category: <CategoryIcon fontSize="small" />,
   condo: <ApartmentIcon fontSize="small" />,
   property: <HomeWorkIcon fontSize="small" />,
}

const rowDescriptions: Record<PropertyPriceAnalysisRow["key"], string> = {
   category: "Promedio de categoria",
   condo: "Promedio de inmuebles",
   property: "Precio por m²",
}

function ComparisonCell({comparison}: {comparison: PropertyPriceAnalysisRow["comparison"]}) {
   if (!comparison) return <>{PLACEHOLDER}</>

   const icon = getComparisonIcon(comparison.direction)
   const relation =
      comparison.direction === "equal"
         ? "igual a la referencia"
         : `${comparison.direction === "below" ? "debajo" : "encima"} de ${
              comparison.reference === "condo" ? "su condominio" : "su categoría"
           }`

   return (
      <span className={`font-normal ${getComparisonTextColor(comparison.direction)}`}>
         {icon && `${icon} `}
         {formatPercent(comparison.percentDifference)}
         <span className="block text-[11px] font-normal leading-tight text-gray-500 sm:text-xs">
            {relation}
         </span>
      </span>
   )
}

function PropertyPriceComparisonTable({
   rows,
   loading,
   error,
}: {
   rows: PropertyPriceAnalysisRow[]
   loading: boolean
   error: boolean
}) {
   return (
      <div>
         <h3 className="mb-1 font-bold text-blue">Comparativo de precios por metro cuadrado</h3>
         <div className="w-full">
            <table className="w-full table-fixed border-collapse text-xs text-gray-500 sm:text-sm">
               <colgroup>
                  <col className="w-[38%]" />
                  <col className="w-[18%]" />
                  <col className="w-[23%]" />
                  <col className="w-[21%]" />
               </colgroup>
               <thead>
                  <tr className="bg-blue text-white">
                     <th className="border border-blue px-1 py-1 text-center font-semibold sm:px-2 sm:py-2">
                        Referencia
                     </th>
                     <th className="border border-blue px-1 py-1 text-center font-semibold sm:px-2 sm:py-2">
                        Precio m²
                     </th>
                     <th className="border border-blue px-1 py-1 text-center font-semibold sm:px-2 sm:py-2">
                        Vs. ref.
                     </th>
                     <th className="border border-blue px-1 py-1 text-center font-semibold sm:px-2 sm:py-2">
                        Total
                     </th>
                  </tr>
               </thead>
               <tbody>
                  {loading &&
                     ["category", "condo", "property"].map((key) => (
                        <tr key={key} className="bg-white">
                           {[0, 1, 2, 3].map((cell) => (
                              <td
                                 key={cell}
                                 className="border border-gray-200 px-1 py-2 sm:px-2 sm:py-3"
                              >
                                 <span className="block h-3 animate-pulse bg-gray-100" />
                              </td>
                           ))}
                        </tr>
                     ))}
                  {!loading &&
                     rows.map((row) => (
                        <tr key={row.key} className="bg-white">
                           <td className="border border-gray-200 px-1 py-1 sm:px-2 sm:py-2">
                              <div className="flex items-start gap-1 sm:items-center sm:gap-2">
                                 <span className={`shrink-0 ${rowColorClassName[row.key]}`}>
                                    {rowIcons[row.key]}
                                 </span>
                                 <div className="min-w-0">
                                    <p
                                       className={`break-words font-normal ${
                                          rowColorClassName[row.key]
                                       }`}
                                    >
                                       {row.label || PLACEHOLDER}
                                    </p>
                                    <span className="block break-words text-[11px] leading-tight text-gray-500 sm:text-xs">
                                       {rowDescriptions[row.key]}
                                    </span>
                                 </div>
                              </div>
                           </td>
                           <td className="break-words border border-gray-200 px-1 py-1 text-center text-sm font-normal sm:px-2 sm:py-2 sm:text-lg">
                              {formatMoney(row.pricePerMeter)}
                           </td>
                           <td className="break-words border border-gray-200 px-1 py-1 text-center sm:px-2 sm:py-2">
                              <ComparisonCell comparison={row.comparison} />
                           </td>
                           <td className="break-words border border-gray-200 px-1 py-1 text-center sm:px-2 sm:py-2">
                              <span className="block text-sm font-normal sm:text-lg">
                                 {formatNumber(row.count)}
                              </span>
                              <span className="text-[11px] leading-tight text-gray-500 sm:text-xs">
                                 {row.countLabel || PLACEHOLDER}
                              </span>
                           </td>
                        </tr>
                     ))}
                  {!loading && error && (
                     <tr>
                        <td
                           className="border border-gray-200 px-2 py-4 text-center text-red"
                           colSpan={4}
                        >
                           No se pudo cargar el análisis de precios.
                        </td>
                     </tr>
                  )}
               </tbody>
            </table>
         </div>
         <p className="mt-2 text-xs font-normal text-gray-500 sm:text-sm">
            Los precios están calculados con los inmuebles activos en tiempo real.
         </p>
      </div>
   )
}

export default PropertyPriceComparisonTable
